import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Label } from "@/components/ui/label";
import { SearchableMultiSelect } from "@/components/ui/searchable-multi-select";
import { toast } from "sonner";

/**
 * Which pickup time ranges are currently offered — admin configuration, held in
 * Mongo and served by /pickup-requests/time-ranges.
 *
 * Shared by the admin and seller Pickup Requests pages so the six slots and the
 * fetch/save logic are defined once. The backend is the source of truth for both
 * the list AND for whether this caller may edit it (can_edit), so neither page
 * reimplements the role rules — and the PUT enforces it again regardless.
 *
 * This is the PICKUP slot list. It is unrelated to utils/appointmentUtils
 * TIME_RANGES, which is the Delhivery appointment list.
 */
export function usePickupTimeRanges() {
  const [config, setConfig] = useState({
    supported: [], enabled: [], options: [], can_edit: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pickup-requests/time-ranges`);
      setConfig(res.data || {});
    } catch {
      // Leaving options empty is the safe failure: the create form then refuses
      // rather than offering slots the server may reject.
      toast.error("Could not load pickup time ranges");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (values) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/pickup-requests/time-ranges`,
                                  { time_ranges: values });
      setConfig((c) => ({ ...c, ...res.data }));
      toast.success("Pickup time ranges updated");
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update time ranges");
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { config, loading, saving, save, reload: load };
}

/**
 * Options for a Time Range dropdown.
 *
 * `current` is the value already on the record being edited. If it is no longer
 * enabled it is still offered, marked "(Inactive)", so an old request stays
 * editable and its stored value is never silently rewritten. In a create form
 * `current` is empty and nothing extra appears.
 */
export function pickupTimeRangeOptions(config, current) {
  const options = [...(config?.options || [])];
  if (current && !options.some((o) => o.value === current)) {
    const known = (config?.supported || []).find((o) => o.value === current);
    options.unshift({ value: current, label: `${known?.label || current} (Inactive)` });
  }
  return options;
}

/**
 * The header control. Read-only for anyone the backend says cannot edit — it
 * renders the same information as plain text rather than a disabled input the
 * user might keep clicking.
 */
export function PickupTimeRangeSetting({ config, saving, onSave }) {
  const canEdit = !!config?.can_edit;
  const supported = config?.supported || [];
  const enabled = config?.enabled || [];

  if (!canEdit) {
    const text = enabled.length
      ? (config.options || []).map((o) => o.label).join(", ")
      : "None configured";
    return (
      <div className="min-w-[200px]">
        <Label className="text-xs text-slate-500">Time Range</Label>
        <p className="mt-1 text-sm text-slate-700" title={text}>{text}</p>
      </div>
    );
  }

  return (
    <div className="min-w-[220px]">
      <Label className="text-xs text-slate-500">Time Range</Label>
      <SearchableMultiSelect
        className="mt-1"
        values={enabled}
        options={supported}
        onChange={onSave}
        placeholder={saving ? "Saving…" : "Select time ranges"}
        emptyText="No time ranges."
      />
    </div>
  );
}
